import * as React from 'react'
import {useState} from 'react'
import {useSelector} from 'react-redux'

import {Modal} from '@ua/design-system/components/modal'
import {Spinner} from '@ua/design-system/components/spinner'

import getContext from 'airship/modules/page-context'

import {
  DEFAULT_CONTACT_SUPPORT_DETAILS,
  DEFAULT_IMAGE_DETAILS,
} from '../constants'
import detailsSlice from '../detailsSlice'
import {productsSelect} from '../products'
import {selectDetails, selectLoading} from '../selectors'
import {useInProductSupportDispatch} from '../store'
import {createTicketThunk} from '../thunks'
import {ContactSupportDetails, Context, ImageDetails, Urgency} from '../types'
import ContactSupportModalContent from './ContactSupportModalContent'
import FooterActions from './FooterActions'
import ThankYou from './thankYou'

export interface Props {
  isOpen: boolean
  onClose: () => void
}

const ContactSupportModal: React.FC<Props> = ({isOpen, onClose}) => {
  const [showThankYou, setShowThankYou] = React.useState<boolean>(false)
  const [isValid, setIsValid] = React.useState<boolean>()
  const context = getContext<Context>()

  const [fieldError, setFieldError] = React.useState<string>('')

  const loading = useSelector(selectLoading)

  const dispatch = useInProductSupportDispatch()

  const [details, setDetails] = useState<ContactSupportDetails>({
    subject: undefined,
    issueDescription: undefined,
    product: context.project ? 'wallet' : '',
    urgency: Urgency.Low,
    image: DEFAULT_IMAGE_DETAILS,
  })
  const stateDetails = useSelector(selectDetails)

  const handleClose = (): void => {
    setShowThankYou(false)
    dispatch(detailsSlice.actions.clearFields())
    setFieldError('')
    onClose()
  }

  const handleDetailsReset = (): void => {
    setDetails(DEFAULT_CONTACT_SUPPORT_DETAILS)
  }

  const handleDetailsChange = (
    key: keyof ContactSupportDetails,
    value: string | ImageDetails
  ): void => {
    setDetails((prevDetails) => ({
      ...prevDetails,
      [key]: value,
    }))
  }

  const handleSend = async (details: ContactSupportDetails): Promise<void> => {
    const {image, ...data} = details
    const content = {
      ...stateDetails,
      ...data,
    }

    dispatch(detailsSlice.actions.clearErrorMessage())

    const findProductLabel = productsSelect.find(
      (prod) => content.product === prod.id
    )

    if (findProductLabel !== undefined) {
      content.productLabel = findProductLabel.label
    }

    if (image.data) content.image = image.data
    if (image.name) content.imageName = image.name

    setFieldError('')
    const result = await dispatch(createTicketThunk(content))
    if (createTicketThunk.fulfilled.match(result)) {
      setShowThankYou(true)
      handleDetailsChange('image', DEFAULT_IMAGE_DETAILS)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      footer={
        loading ? null : (
          <FooterActions
            onCancel={handleClose}
            submitLabel="Contact Support"
            cancelLabel={showThankYou ? 'Close' : 'Cancel'}
            onSubmit={showThankYou ? undefined : () => handleSend(details)}
            isDisabled={!isValid || fieldError !== ''}
          />
        )
      }
      heading="Contact Support"
      onClose={handleClose}
      size="md"
      closeOnOverlayClick={false}
    >
      {loading && (
        <div className="u-flex u-flex-justify-center u-flex-align-items-center">
          <Spinner />
        </div>
      )}

      {!loading && showThankYou && <ThankYou />}

      {!loading && !showThankYou && (
        <ContactSupportModalContent
          handleIsValid={setIsValid}
          details={details}
          onChange={handleDetailsChange}
          onReset={handleDetailsReset}
        />
      )}
    </Modal>
  )
}

export default ContactSupportModal
